import React, { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  CheckCircle,
  Download,
  Lock,
  MessageSquare,
  Pin,
  Plus,
  Trash2,
  X,
  History,
  Maximize,
  RefreshCw,
} from "lucide-react";
import DesignDetails from "./components/DesignDetails";
import DesignMemberSelector from "./components/DesignMemberSelector";
import DesignTaskLinkSelector from "./components/DesignTaskLinkSelector";
import ImageZoomModal from "./components/ImageZoomModal";
import {
  approveDesignById,
  createCommentForDesign,
  createPinForDesign,
  deleteCommentById,
  deleteDesignById,
  downloadDiagram,
  getDesignById,
  fetchCommentsForDesign,
  fetchDesignVersions,
  fetchPinsForDesign,
  fetchLinkedTasks,
  getStatusBadgeClass,
  lockDesignById,
  replaceDesignFile,
  updatePinById,
  deletePinById,
  resolvePinById,
  reopenDesign,
} from "../../../api/design/service";
import { useAlert } from "components/UI_components/Alert";
import {
  normalizeFileUrl,
  displayNameFromOrgMember,
  useLoadAssigneesAndApprovers,
} from "./DesignShared";

export default function ImageDetailView({
  drawing,
  onBack,
  onUpdate,
  onDelete,
  currentMember,
}) {
  const { addAlert } = useAlert();
  const {
    orgMembers,
    orgMembersLoading,
    designMembers,
    designMembersLoading,
    refreshDesignMembers,
  } = useLoadAssigneesAndApprovers({ drawing, onUpdate });

  const [activePinId, setActivePinId] = useState(null);
  const [isPinMode, setIsPinMode] = useState(false);
  const [newPinComment, setNewPinComment] = useState("");
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pendingPin, setPendingPin] = useState(null);
  const [pinAssignees, setPinAssignees] = useState([]);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolvePinId, setResolvePinId] = useState(null);
  const [resolveComment, setResolveComment] = useState("");
  const [commentText, setCommentText] = useState("");

  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [isReplacing, setIsReplacing] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isReopening, setIsReopening] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const fileInputRef = useRef(null);
  const [isCreatingPin, setIsCreatingPin] = useState(false);

  // Local state for comments and pins
  const [comments, setComments] = useState([]);
  const [pins, setPins] = useState([]);

  const isLocked = drawing.status === "Locked";
  const isDraft = drawing.status === "Draft";
  const openIssuesCount = pins.filter((p) => p.status === "pending").length;
  const canDesignAdmin =
    currentMember?.design_admin === true ||
    currentMember?.joined_as === "owner" ||
    currentMember?.is_admin === true;

  const [showAddAssignee, setShowAddAssignee] = useState(false);
  const [showLinkToTask, setShowLinkToTask] = useState(false);
  const [linkedTasks, setLinkedTasks] = useState([]);
  const siteId = drawing?.site?.id ?? drawing?.site;

  // Fetch comments
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!drawing?.id) return;
      try {
        const list = await fetchCommentsForDesign(drawing.id);
        if (!mounted) return;
        const mapped = (list || []).map((c, idx) => ({
          id: c.id || c.pk || `${drawing.id}-c-${idx}`,
          text: c.comment || c.text || "",
          author: c.created_by || "Unknown",
          pin_linked: c.pin || null,
          createdAt: c.created_on || new Date().toISOString(),
          isResolved: !!c.is_resolved || !!c.isResolved || false,
        }));
        setComments(mapped);
      } catch (err) {
        console.error("Failed to fetch comments", err);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [drawing?.id, refreshTick]);

  // Fetch pins
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!drawing?.id) return;
      try {
        const list = await fetchPinsForDesign(drawing.id);
        if (!mounted) return;
        const mapped = (list || []).map((p, idx) => ({
          ...p,
          id: p.id || p.pk || `${drawing.id}-p-${idx}`,
          x:
            typeof p.x_coordinate !== "undefined"
              ? Number(p.x_coordinate)
              : typeof p.x !== "undefined"
                ? Number(p.x)
                : 0,
          y:
            typeof p.y_coordinate !== "undefined"
              ? Number(p.y_coordinate)
              : typeof p.y !== "undefined"
                ? Number(p.y)
                : 0,
          comment: p.comment || p.text || "",
          status: p.status || "pending",
          author:
            (p.created_by &&
              (typeof p.created_by === "string"
                ? p.created_by
                : p.created_by.username || p.created_by.name)) ||
            (p.uploaded_by &&
              (typeof p.uploaded_by === "string"
                ? p.uploaded_by
                : p.uploaded_by.username || p.uploaded_by.name)) ||
            p.author ||
            "Unknown",
          createdAt: p.created_at || p.createdAt || new Date().toISOString(),
          assignees: p.assignees || [],
        }));
        setPins(mapped);
      } catch (err) {
        console.error("Failed to fetch pins", err);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [drawing?.id, refreshTick]);

  // Fetch versions when modal opens (or design changes)
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!showVersionHistory || !drawing?.id) return;
      try {
        setVersionsLoading(true);
        setVersionsError(null);
        const list = await fetchDesignVersions(drawing.id);
        if (!mounted) return;
        setVersions(list);
        onUpdate((prev) => ({ ...(prev || drawing), versions: list }));
      } catch (err) {
        if (!mounted) return;
        console.error("Fetch version history failed", err);
        setVersionsError(err?.message || "Failed to fetch version history");
        setVersions([]);
      } finally {
        if (mounted) setVersionsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [showVersionHistory, drawing.id, refreshTick]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!drawing?.id) return;
      try {
        const list = await fetchLinkedTasks(drawing.id);
        if (!mounted) return;
        setLinkedTasks(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!mounted) return;
        setLinkedTasks([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [drawing?.id, showLinkToTask, refreshTick]);

  const handleRefreshAll = async () => {
    if (!drawing?.id) return;
    try {
      setIsRefreshing(true);
      const fresh = await getDesignById(drawing.id);
      onUpdate((prev) => ({ ...(prev || drawing), ...(fresh || {}) }));
      setRefreshTick((prev) => prev + 1);
      try {
        await refreshDesignMembers?.();
      } catch (_) {
        // best-effort refresh for members
      }
    } catch (err) {
      console.error("Refresh failed", err);
      addAlert(err?.message || "Failed to refresh design data", "error");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCanvasClick = (e) => {
    if (!isPinMode || isLocked) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPendingPin({ x, y });
    setShowPinDialog(true);
  };

  const handleAddPin = () => {
    (async () => {
      if (!pendingPin || !newPinComment.trim()) return;
      try {
        setIsCreatingPin(true);
        const payload = {
          x_coordinate: Math.round(pendingPin.x),
          y_coordinate: Math.round(pendingPin.y),
          comment: newPinComment.trim(),
          assignees: pinAssignees,
        };
        const created = await createPinForDesign(drawing.id, payload);

        const mapped = {
          id: created.id || created.pk || `p${Date.now()}`,
          x:
            typeof created.x_coordinate !== "undefined"
              ? Number(created.x_coordinate)
              : typeof created.x !== "undefined"
                ? Number(created.x)
                : pendingPin.x,
          y:
            typeof created.y_coordinate !== "undefined"
              ? Number(created.y_coordinate)
              : typeof created.y !== "undefined"
                ? Number(created.y)
                : pendingPin.y,
          comment: created.comment || created.text || newPinComment.trim(),
          status: created.status || "pending",
          author:
            (created.created_by &&
              (typeof created.created_by === "string"
                ? created.created_by
                : created.created_by.username || created.created_by.name)) ||
            (created.uploaded_by &&
              (typeof created.uploaded_by === "string"
                ? created.uploaded_by
                : created.uploaded_by.username || created.uploaded_by.name)) ||
            created.author ||
            "Unknown",
          createdAt:
            created.created_at || created.createdAt || new Date().toISOString(),
        };

        setPins((prev) => [...prev, mapped]);

        try {
          const list = await fetchPinsForDesign(drawing.id);
          const normalized = (list || []).map((p, idx) => ({
            ...p,
            id: p.id || p.pk || `${drawing.id}-p-${idx}`,
            x:
              typeof p.x_coordinate !== "undefined"
                ? Number(p.x_coordinate)
                : typeof p.x !== "undefined"
                  ? Number(p.x)
                  : 0,
            y:
              typeof p.y_coordinate !== "undefined"
                ? Number(p.y_coordinate)
                : typeof p.y !== "undefined"
                  ? Number(p.y)
                  : 0,
            comment: p.comment || p.text || "",
            status: p.status || "pending",
            author:
              (p.created_by &&
                (typeof p.created_by === "string"
                  ? p.created_by
                  : p.created_by.username || p.created_by.name)) ||
              (p.uploaded_by &&
                (typeof p.uploaded_by === "string"
                  ? p.uploaded_by
                  : p.uploaded_by.username || p.uploaded_by.name)) ||
              p.author ||
              "Unknown",
            createdAt: p.created_at || p.createdAt || new Date().toISOString(),
          }));
          setPins(normalized);
        } catch (refreshErr) {
          console.warn("Refresh pins after create failed", refreshErr);
        }

        setNewPinComment("");
        setPinAssignees([]);
        setPendingPin(null);
        setShowPinDialog(false);
        setIsPinMode(false);
        addAlert("Pin added successfully", "success");
      } catch (err) {
        console.error("Create pin failed", err);
        addAlert(err?.message || "Failed to add pin", "error");
      } finally {
        setIsCreatingPin(false);
      }
    })();
  };

  const handleResolvePin = async (pinId) => {
    setResolvePinId(pinId);
    setResolveComment("");
    setShowResolveModal(true);
  };

  const handleConfirmResolve = async () => {
    if (!resolvePinId) return;
    try {
      await resolvePinById(resolvePinId, { comment: resolveComment.trim() });
      setPins((prev) =>
        prev.map((p) =>
          p.id === resolvePinId ? { ...p, status: "solved" } : p,
        ),
      );
      setResolvePinId(null);
      setShowResolveModal(false);
      setActivePinId(null);
      addAlert("Issue marked as resolved", "success");
    } catch (err) {
      console.error("Resolve pin failed", err);
      addAlert(err?.message || "Failed to resolve pin", "error");
    }
  };
  const handleDeletePin = async (pinId) => {
    addAlert("Are you sure you want to delete this pin?", "confirm", {
      onConfirm: async () => {
        try {
          await deletePinById(pinId);
          setPins((prev) => prev.filter((p) => p.id !== pinId));
          setActivePinId(null);
          addAlert("Pin deleted successfully", "success");
        } catch (err) {
          console.error("Delete pin failed", err);
          addAlert(err?.message || "Failed to delete pin", "error");
        }
      },
    });
  };

  const handleApprove = async () => {
    try {
      setIsApproving(true);
      await approveDesignById(drawing.id);
      onUpdate((prev) => ({ ...(prev || drawing), status: "Approved" }));
      addAlert("Drawing approved successfully", "success");
    } catch (err) {
      console.error("Approve failed", err);
      addAlert(err?.message || "Failed to approve drawing", "error");
    } finally {
      setIsApproving(false);
    }
  };

  const handleLock = async () => {
    try {
      setIsLocking(true);
      await lockDesignById(drawing.id);
      onUpdate((prev) => ({ ...(prev || drawing), status: "Locked" }));
      setIsPinMode(false);
      addAlert("Drawing locked successfully", "success");
    } catch (err) {
      console.error("Lock failed", err);
      addAlert(err?.message || "Failed to lock drawing", "error");
    } finally {
      setIsLocking(false);
    }
  };

  const handleAddComment = () => {
    (async () => {
      if (!commentText.trim()) return;
      try {
        const created = await createCommentForDesign(
          drawing.id,
          commentText.trim(),
        );
        const mapped = {
          id: created.id || created.pk || `c${Date.now()}`,
          text: created.comment || created.text || commentText.trim(),
          author: created.created_by || "Unknown",
          pin_linked: created.pin || null,
          createdAt:
            created.created_at || created.createdAt || new Date().toISOString(),
          isResolved: !!created.is_resolved || !!created.isResolved || false,
        };
        setComments((prev) => [...prev, mapped]);
        setCommentText("");
        addAlert("Comment added", "success");
      } catch (err) {
        console.error("Create comment failed", err);
        addAlert(err?.message || "Failed to add comment", "error");
      }
    })();
  };

  const handleResolveComment = (commentId) => {
    // Ideally this should call an API, but the existing code only updated local state.
    // Assuming resolving comment is just a UI toggle for now or maybe missing API integration in previous code?
    // The previous code was: onUpdate((prev) => ({ comments: ... isResolved: true ... }))
    // I will keep it as local state update for now, but really should be an API call if persistance is needed.
    // Wait, previous code had `createCommentForDesign` but `handleResolveComment` only updated state.
    // Checking previous code... yes, handleResolveComment only did onUpdate.
    // And there is `isResolved` property.
    // I'll stick to local state update but maybe I should check if there is an API for it?
    // The user didn't ask to fix this, just refactor.
    setComments((prev) =>
      prev.map((c) => (c.id === commentId ? { ...c, isResolved: true } : c)),
    );
    addAlert("Comment resolved", "success");
  };

  const handleConfirmDeleteDesign = () => {
    addAlert("Are you sure you want to delete this design?", "confirm", {
      onConfirm: async () => {
        try {
          await deleteDesignById(drawing.id);
          if (onDelete) onDelete(drawing.id);
          addAlert("Design deleted", "success");
          onBack();
        } catch (err) {
          console.error("Delete design failed", err);
          addAlert(err?.message || "Failed to delete design", "error");
        }
      },
    });
  };

  const handleReopenClick = async () => {
    try {
      setIsReopening(true);
      const reopened = await reopenDesign(drawing.id);
      const updated = {
        ...drawing,
        status: "Open",
        lastUpdated: new Date().toISOString().split("T")[0],
      };
      onUpdate((prev) => ({ ...(prev || drawing), ...updated }));
      addAlert("Design reopened successfully", "success");
    } catch (err) {
      console.error("Reopen design failed", err);
      addAlert(err?.message || "Failed to reopen design", "error");
    } finally {
      setIsReopening(false);
    }
  };
  const handleReplaceClick = () => {
    if (isLocked) return;
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleReplaceSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    try {
      setIsReplacing(true);
      const replaced = await replaceDesignFile(drawing.id, file);

      // After replace, hydrate from retrieve API to always get the latest file URL.
      try {
        const fresh = await getDesignById(drawing.id);
        onUpdate((prev) => ({ ...(prev || drawing), ...(fresh || {}) }));
      } catch (_) {
        const newDiagramRaw =
          replaced?.diagram ||
          replaced?.fileUrl ||
          replaced?.file_url ||
          replaced?.url ||
          drawing.diagram;
        const newDiagram = normalizeFileUrl(newDiagramRaw);
        const updated = {
          ...drawing,
          diagram: newDiagram,
          lastUpdated: (
            replaced?.last_updated_on ||
            replaced?.lastUpdated ||
            drawing.lastUpdated ||
            new Date().toISOString()
          )
            .toString()
            .split("T")[0],
        };
        onUpdate((prev) => ({ ...(prev || drawing), ...updated }));
      }
      addAlert("File replaced successfully", "success");
    } catch (err) {
      console.error("Replace file failed", err);
      addAlert(err?.message || "Failed to replace file", "error");
    } finally {
      setIsReplacing(false);
    }
  };

  const handleViewVersion = (version) => {
    console.log(version);
    if (version.fileType === "pdf") {
      onUpdate((prev) => ({
        ...prev,
        version_number: version.version,
        status: version.status,
        uploaded_by: version.uploadedBy,
        diagram: version.fileUrl,
        file_type: version.fileType,
      }));
    } else if (
      ["image", "jpeg", "png", "jpg", "svg"].includes(version.fileType)
    ) {
      onUpdate((prev) => ({
        ...prev,
        diagram: version.fileUrl,
        file_type: version.fileType,
        version_number: version.version,
        status: version.status,
        uploaded_by: version.uploadedBy,
      }));
    } else if (version.fileType.toLowerCase() === "dwg") {
      onUpdate((prev) => ({
        ...prev,
        diagram: version.fileUrl,
        file_type: version.fileType,
        version_number: version.version,
        status: version.status,
        urn: version.aps_urn,
        uploaded_by: version.uploadedBy,
      }));
    }
    setShowVersionHistory(false);
  };

  const handleShowCurrentVersion = async () => {
    if (!drawing?.id) return;
    try {
      const fresh = await getDesignById(drawing.id);
      onUpdate((prev) => ({ ...(prev || drawing), ...(fresh || {}) }));

      try {
        const list = await fetchLinkedTasks(drawing.id);
        setLinkedTasks(Array.isArray(list) ? list : []);
      } catch (_) {
        // best-effort; actions can still render correctly from fresh status
      }
    } catch (err) {
      console.error("Failed to refresh current version", err);
      addAlert(err?.message || "Failed to load current version", "error");
    }
  };

  return (
    <div className="flex-1 w-full h-[90vh] overflow-y-scroll flex flex-col p-4 gap-3 bg-gray-50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            className="px-2 py-1 border rounded flex items-center gap-2 cursor-pointer hover:bg-sm-accent hover:text-white"
            onClick={onBack}
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <span>Design</span>
              <span>{">"}</span>
              <span>{drawing.name}</span>
            </div>
            {/* <div className="flex items-center gap-2">
                            <h1 className="text-lg font-bold">{drawing.name} (v{drawing.version_number})</h1>
                            <span className={`px-2 py-0.5 rounded text-xs ${getStatusBadgeClass(drawing.status)}`}>{drawing.status}</span>
                        </div> */}
            <div className="flex items-center gap-2 text-lg font-bold">
              {drawing.name} (v{drawing.version_number})
              {drawing.original_version === drawing.version_number && (
                <span className="ml-2 text-xs font-medium bg-sm-secondary/10 px-2 py-1 rounded-lg text-sm-secondary">
                  Latest Version
                </span>
              )}
              <span
                className={`px-2 py-0.5 rounded text-xs ${getStatusBadgeClass(drawing.status)}`}
              >
                {drawing.status}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {drawing.version_number !== drawing.original_version && (
            <button
              className="px-2 py-1 border rounded flex items-center gap-2 bg-white"
              onClick={() => handleShowCurrentVersion()}
            >
              <History className="w-4 h-4" /> Show Current Version
            </button>
          )}
          {!isLocked && canDesignAdmin && (
            <button
              className={`px-2 py-1 border rounded flex items-center gap-2 bg-white ${isPinMode ? "bg-gray-200" : ""}`}
              onClick={() => setIsPinMode(!isPinMode)}
            >
              <Pin className="w-4 h-4" /> {isPinMode ? "Cancel" : "Pin"}
            </button>
          )}
          <button
            className="px-2 py-1 border rounded flex items-center gap-2 bg-white"
            onClick={() => setShowZoomModal(true)}
            title="Zoom Image"
          >
            <Maximize className="w-4 h-4" /> Expand
          </button>
          {/* {!isLocked && (
                        <button
                            className="px-2 py-1 border rounded flex items-center gap-2 bg-white"
                            onClick={() => {
                                const el = document.getElementById("design-comments");
                                if (el && typeof el.scrollIntoView === "function") {
                                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                                }
                            }}
                            type="button"
                        >
                            <MessageSquare className="w-4 h-4" /> Comment
                        </button>
                    )} */}
          {/* <button
                        className="px-2 py-1 border rounded bg-white"
                        onClick={() => {
                            const openIssues = pins.filter((p) => p.status === "pending");
                            if (openIssues.length > 0) openIssues.forEach((issue) => handleResolvePin(issue.id));
                            else addAlert("No pending issues to resolve", "warning");
                        }}
                        disabled={isLocked}
                    >
                        <CheckCircle className="w-4 h-4 inline-block mr-2" /> Resolve Issues
                    </button> */}
          {/* {canDesignAdmin && drawing.status === "Approved" && (
                        <button
                            className="px-2 py-1 border rounded flex items-center gap-2 bg-white disabled:opacity-60"
                            onClick={handleLock}
                            disabled={isLocking}
                        >
                            <Lock className="w-4 h-4" /> {isLocking ? "Locking..." : "Lock"}
                        </button>
                    )} */}
          <button
            className="px-2 py-1 border rounded flex items-center gap-2 bg-white"
            onClick={() => downloadDiagram(drawing.diagram, drawing.name)}
          >
            <Download className="w-4 h-4" /> Download
          </button>
          <button
            className="px-2 py-1 text-red-600 flex items-center gap-2"
            // onClick={() => setShowDeleteConfirm(true)}

            onClick={handleConfirmDeleteDesign}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-9 flex flex-col gap-4 min-w-0">
          <div className="w-full border rounded min-h-[300px] bg-white p-6">
            <div
              className={`w-full h-full bg-gray-50 flex items-center justify-center relative ${isPinMode ? "cursor-crosshair" : ""}`}
              onClick={handleCanvasClick}
            >
              <div className="absolute inset-4 border-2 border-dashed rounded-lg flex items-center justify-center bg-white">
                {drawing.diagram ? (
                  <img
                    src={drawing.diagram}
                    alt={drawing.name}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="text-center p-6">
                    <div className="h-12 w-12 mx-auto mb-2 text-2xl">🖼️</div>
                    <p className="text-gray-500 text-sm">{drawing.name}</p>
                  </div>
                )}
              </div>

              {pins.map((pin) => (
                <div
                  key={pin.id}
                  className="absolute transform -translate-x-1/2 -translate-y-full z-10"
                  style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                >
                  <button
                    className="relative group"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActivePinId(activePinId === pin.id ? null : pin.id);
                    }}
                    type="button"
                  >
                    <Pin
                      className={`w-4 h-4 ${pin.status === "solved" ? "text-green-500" : "text-red-500"}`}
                    />
                    {activePinId === pin.id && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 z-20">
                        <div className="bg-white border p-2 rounded shadow">
                          <div className="flex items-start justify-between mb-1.5">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-medium">Pin</span>
                              {pin.status === "solved" ? (
                                <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded">
                                  SOLVED
                                </span>
                              ) : (
                                <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] font-bold rounded">
                                  PENDING
                                </span>
                              )}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActivePinId(null);
                              }}
                              type="button"
                            >
                              <X className="w-4 h-4 text-gray-500" />
                            </button>
                          </div>
                          <p className="text-xs mb-1.5">{pin.comment}</p>
                          <div className="text-xs text-gray-500 space-y-0.5">
                            <p>By: {pin.author}</p>
                          </div>
                          {pin.status === "pending" &&
                            !isLocked &&
                            (pin.assignees?.includes(currentMember?.id) ||
                              canDesignAdmin) && (
                              <button
                                className="w-full mt-2 p-1 bg-green-600 text-white rounded text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResolvePin(pin.id);
                                }}
                                type="button"
                              >
                                Resolve
                              </button>
                            )}
                          {canDesignAdmin && (
                            <button
                              className="w-full mt-1.5 p-1 border border-red-200 text-red-600 rounded text-xs hover:bg-red-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePin(pin.id);
                              }}
                              type="button"
                            >
                              Delete Pin
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </button>
                </div>
              ))}

              {isPinMode && (
                <div className="absolute top-3 left-3 bg-purple-50 text-purple-700 px-2.5 py-1 rounded text-xs">
                  Click to place pin
                </div>
              )}
            </div>
          </div>

          <div className="border rounded bg-white" id="design-comments">
            <div className="px-3 py-2 border-b">
              <strong>Comments</strong>
            </div>
            <div className="p-2 max-h-44 overflow-auto">
              {comments.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-2">
                  No comments yet
                </p>
              ) : (
                comments.map((comment) => (
                  <div
                    key={comment.id}
                    className={`px-3 py-2 rounded mb-2 text-sm ${comment.isResolved ? "bg-gray-100" : "bg-gray-50"}`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div>
                        <div className="font-medium text-sm">
                          {comment.author}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {comment.text}
                        </p>
                      </div>
                      <div className="text-right ml-4 flex flex-col items-end gap-1">
                        <div className="text-xs text-gray-500 flex items-center gap-2">
                          {comment.pin_linked && (
                            <span
                              onClick={() => setActivePinId(comment.pin_linked)}
                              className="text-sm-primary text-[10px] cursor-pointer bg-sm-primary/10 px-2 py-0.5 rounded "
                            >
                              Linked to Pin #{comment.pin_linked}
                            </span>
                          )}
                          {new Date(comment.createdAt).toLocaleDateString()}
                        </div>
                        {/* <div className="flex items-center gap-2">
                                                    {!comment.isResolved && (
                                                        <button className="text-xs" onClick={() => handleResolveComment(comment.id)} type="button">
                                                            Resolve
                                                        </button>
                                                    )}
                                                    {comment.isResolved && <div className="text-xs text-green-600">Resolved</div>}
                                                    {/* <button className="text-red-600 p-1" title="Delete comment" onClick={() => handleDeleteComment(comment.id)} type="button">
                              <Trash2 className="w-4 h-4" />
                            </button> */}
                        <div className="flex items-center gap-2">
                          {comment.isResolved && (
                            <div className="text-xs text-green-600">
                              Resolved
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="px-3 py-2 border-t flex items-center gap-2">
              <input
                placeholder="Write a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                className="flex-1 border py-2 px-3 rounded text-sm"
                onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
              />
              <button
                className="px-3 py-2 bg-purple-700 text-white rounded text-sm"
                onClick={handleAddComment}
                disabled={!commentText.trim()}
              >
                Send
              </button>
            </div>
          </div>
        </div>

        <aside className="col-span-3 flex flex-col gap-3">
          <DesignDetails
            drawing={drawing}
            designMembers={designMembers}
            designMembersLoading={designMembersLoading}
            orgMembersLoading={orgMembersLoading}
            canDesignAdmin={canDesignAdmin}
            setShowAddAssignee={setShowAddAssignee}
          />

          {drawing.version_number === drawing.original_version && (
            <div className="border rounded p-3 bg-white">
              <div className="text-xs text-gray-500 mb-2">Actions</div>
              <div className="space-y-2">
                {drawing.status !== "Approved" && !isLocked && (
                  <button
                    className="w-full p-2 border rounded disabled:opacity-60"
                    disabled={isReplacing}
                    onClick={handleReplaceClick}
                  >
                    {isReplacing ? "Replacing..." : "Replace File"}
                  </button>
                )}
                {drawing.status !== "Approved" &&
                  !isLocked &&
                  canDesignAdmin && (
                    <button
                      className="w-full p-2 border rounded"
                      onClick={handleApprove}
                      disabled={isApproving || isLocking}
                    >
                      {isApproving ? "Approving..." : "Approve"}
                    </button>
                  )}
                {(drawing.status === "Approved" || isLocked) && (
                  <button
                    className="w-full p-2 border rounded disabled:opacity-60"
                    onClick={handleReopenClick}
                    disabled={isReopening || !canDesignAdmin}
                  >
                    {isReopening ? "Reopening..." : "Reopen"}
                  </button>
                )}
                {drawing.status === "Approved" && canDesignAdmin && (
                  <button
                    className="w-full p-2 border rounded bg-sm-primary text-white hover:bg-sm-primary/90"
                    onClick={() => setShowLinkToTask(true)}
                  >
                    {linkedTasks.length > 0
                      ? `${linkedTasks.length} Tasks linked`
                      : "Link to Task"}
                  </button>
                )}
              </div>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.pdf"
            className="hidden"
            onChange={handleReplaceSelected}
          />

          {openIssuesCount > 0 && (
            <div className="border rounded p-2 bg-red-50">
              <div className="text-xs text-red-600 font-semibold">
                {openIssuesCount} Pending Issue(s)
              </div>
              <div className="text-xs text-gray-500">
                Resolve before locking
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              className="p-2 border rounded bg-white disabled:opacity-60"
              onClick={handleRefreshAll}
              disabled={isRefreshing}
              title="Refresh"
              type="button"
            >
              <RefreshCw
                className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
              />
            </button>
            <button
              className="flex-1 p-2 border rounded"
              onClick={() => setShowVersionHistory(true)}
              type="button"
            >
              Version History
            </button>
          </div>
        </aside>
      </div>

      {/* Design Member Selector Modal */}
      <DesignMemberSelector
        show={showAddAssignee}
        onClose={() => setShowAddAssignee(false)}
        designId={drawing.id}
        orgMembers={orgMembers}
        orgMembersLoading={orgMembersLoading}
        currentDesignMembers={designMembers}
        onUpdate={refreshDesignMembers}
      />

      <DesignTaskLinkSelector
        show={showLinkToTask}
        onClose={() => setShowLinkToTask(false)}
        designId={drawing.id}
        siteId={siteId}
        onLinked={onUpdate}
      />

      {showPinDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded w-full max-w-md">
            <h3 className="font-semibold mb-2">Add Comment Pin</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">
                  Comment
                </label>
                <textarea
                  placeholder="Enter your comment or issue description..."
                  value={newPinComment}
                  onChange={(e) => setNewPinComment(e.target.value)}
                  rows={3}
                  className="w-full border p-2 rounded text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">
                  Assignees
                </label>
                <div className="border rounded p-2 max-h-32 overflow-y-auto space-y-1">
                  {(designMembers || []).map((m) => (
                    <label
                      key={m.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={pinAssignees.includes(m.member_id || m.id)}
                        onChange={(e) => {
                          const id = m.member_id || m.id;
                          if (e.target.checked) {
                            setPinAssignees((prev) => [...prev, id]);
                          } else {
                            setPinAssignees((prev) =>
                              prev.filter((pid) => pid !== id),
                            );
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      <span className="text-xs">
                        {displayNameFromOrgMember(m)}
                      </span>
                    </label>
                  ))}
                  {(designMembers || []).length === 0 && (
                    <span className="text-xs text-gray-400">
                      No members to assign
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-3 py-1 border rounded text-sm"
                onClick={() => {
                  setShowPinDialog(false);
                  setPendingPin(null);
                  setNewPinComment("");
                  setPinAssignees([]);
                }}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1 bg-purple-700 text-white rounded text-sm disabled:opacity-60"
                onClick={handleAddPin}
                disabled={!newPinComment.trim() || isCreatingPin}
              >
                {isCreatingPin ? "Adding..." : "Add Pin"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Pin Modal */}
      {showResolveModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[200]">
          <div className="bg-white p-4 rounded w-full max-w-md shadow-xl">
            <h3 className="font-semibold mb-2">Resolve Pin</h3>
            <p className="text-xs text-gray-500 mb-2">
              Optional: add a comment about how this was resolved.
            </p>
            <textarea
              placeholder="Resolution notes..."
              value={resolveComment}
              onChange={(e) => setResolveComment(e.target.value)}
              rows={3}
              className="w-full border p-2 rounded text-sm mb-3"
            />
            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-1 border rounded text-sm"
                onClick={() => {
                  setShowResolveModal(false);
                  setResolvePinId(null);
                  setResolveComment("");
                }}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1 bg-green-600 text-white rounded text-sm"
                onClick={handleConfirmResolve}
              >
                Resolve
              </button>
            </div>
          </div>
        </div>
      )}

      {showVersionHistory && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded w-full max-w-lg">
            <h3 className="font-semibold mb-2">Version History</h3>
            <div className="space-y-2 max-h-80 overflow-auto">
              {versionsLoading && (
                <div className="text-sm text-gray-500 p-3">
                  Loading versions...
                </div>
              )}
              {!versionsLoading && versionsError && (
                <div className="text-sm text-red-600 p-3">{versionsError}</div>
              )}
              {!versionsLoading &&
                !versionsError &&
                (versions || []).length === 0 && (
                  <div className="text-sm text-gray-500 p-3">
                    No versions found.
                  </div>
                )}
              {!versionsLoading &&
                !versionsError &&
                (versions || []).map((version) => (
                  <div
                    key={version.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="text-xs font-bold bg-gray-200 px-2 py-1 rounded shrink-0">
                        {version.version}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm truncate">{version.name}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {version.uploadedBy || drawing.uploadedBy} •{" "}
                          {version.uploadDate || drawing.lastUpdated}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${getStatusBadgeClass(version.status)}`}
                      >
                        {version.status}
                      </span>
                      <button
                        className="px-2 py-1 text-sm border rounded hover:bg-sm-accent hover:text-white"
                        disabled={!version.fileUrl}
                        onClick={() => handleViewVersion(version)}
                      >
                        View
                      </button>
                      <button
                        className="px-2 py-1 text-sm border rounded disabled:opacity-60"
                        disabled={!version.fileUrl}
                        onClick={() =>
                          downloadDiagram(
                            version.fileUrl,
                            `${drawing.name}_${version.version}`,
                          )
                        }
                      >
                        Download
                      </button>
                    </div>
                  </div>
                ))}
            </div>
            <div className="flex justify-end mt-3">
              <button
                className="px-3 py-1 border rounded"
                onClick={() => setShowVersionHistory(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ImageZoomModal
        isOpen={showZoomModal}
        onClose={() => setShowZoomModal(false)}
        imageUrl={drawing.diagram}
        itemName={drawing.name}
      />
    </div>
  );
}
